import clsx from 'clsx'

// Types
import type { CSSProperties } from 'react'
import type { PortableTextBlock } from '@portabletext/react'
import type { SanityButtonProps } from '~/types'
import {
  Button,
  ContentEditor,
  IconRewardsFalse,
  IconRewardsTrue,
  IconStar,
  IconTrophy,
} from '~/components'

type TierColumnProps = {
  header: string
  showIcon: boolean
  icon: 'star' | 'trophy'
  textColour:
    | 'neutral'
    | 'tealGreen'
    | 'forestGreen'
    | 'emeraldGreen'
    | 'alertOrange'
  /** Optional per-column width in `fr` units. Defaults to 1 when blank. */
  width?: number
}

type TierCellProps = {
  type: 'text' | 'boolean'
  content: string
  check: boolean
  centerAlign?: boolean
}

type TierRowProps = {
  cells: TierCellProps[]
}

type TierFooterProps = {
  text: string
}

type TieredTableProps = {
  topContent: PortableTextBlock[]
  table: {
    columnCount: number
    columns: TierColumnProps[]
    rows: TierRowProps[]
    /** Optional — when empty / missing, the footer row is skipped entirely. */
    footer?: TierFooterProps[]
  }
  button: SanityButtonProps
}

const getIconColorClasses = (color: string) => ({
  '[&_rect]:!fill-neutral-900': color === 'neutral',
  '[&_rect]:!fill-teal-green': color === 'tealGreen',
  '[&_rect]:!fill-forest-green': color === 'forestGreen',
  '[&_rect]:!fill-emerald-green': color === 'emeraldGreen',
  '[&_rect]:!fill-alert-orange': color === 'alertOrange',
})

const getTextColorClasses = (color: string) => ({
  'text-neutral-900': color === 'neutral',
  'text-teal-green': color === 'tealGreen',
  'text-forest-green': color === 'forestGreen',
  'text-emerald-green': color === 'emeraldGreen',
  'text-alert-orange': color === 'alertOrange',
})

/*
 * Builds the `grid-template-columns` string from the editor-configured
 * width on each column. Each column contributes `${width}fr`; falsy or
 * non-positive widths default to `1fr` so the table renders even if an
 * editor hasn't set widths yet.
 */
const buildGridTemplate = (columns: TierColumnProps[]): string =>
  columns
    .map(c => `${c.width && c.width > 0 ? c.width : 1}fr`)
    .join(' ')

const TierColumn = ({
  header,
  showIcon,
  icon,
  textColour,
}: TierColumnProps) => {
  const IconComponent = icon === 'star' ? IconStar : IconTrophy

  return (
    <div className="flex flex-col items-center justify-end first-of-type:items-start">
      {showIcon && (
        <IconComponent
          className={clsx('size-12', getIconColorClasses(textColour))}
        />
      )}

      <div
        className={clsx(
          'text-center text-lg font-bold md:text-xl',
          getTextColorClasses(textColour)
        )}
      >
        {header}
      </div>
    </div>
  )
}

const TierRow = ({
  cells,
  gridStyle,
}: TierRowProps & { gridStyle: CSSProperties }) => (
  <div
    className="grid min-h-12"
    style={gridStyle}
  >
    {cells.map((cell, j) => (
      <TierRowCell
        key={j}
        {...cell}
      />
    ))}
  </div>
)

const TierRowCell = ({
  type,
  content,
  check,
  centerAlign = false,
}: TierCellProps) => (
  <div
    className={clsx('flex items-center first-of-type:font-bold', {
      'justify-center': type !== 'text',
      'justify-center text-center': type === 'text' && centerAlign,
    })}
  >
    {type === 'text' ? (
      <span>{content}</span>
    ) : check ? (
      <IconRewardsTrue className="size-6 text-teal-green" />
    ) : (
      <IconRewardsFalse className="size-6 text-neutral-400" />
    )}
  </div>
)

const TierFooter = ({ text }: TierFooterProps) => (
  <span className="text-center text-xs text-neutral-500 first-of-type:text-left first-of-type:font-bold">
    {text}
  </span>
)

export default function BlockTieredTable({
  topContent,
  table,
  button,
}: TieredTableProps) {
  const gridStyle: CSSProperties = {
    gridTemplateColumns: buildGridTemplate(table.columns),
  }

  const hasFooter = Array.isArray(table.footer) && table.footer.length > 0

  // Tables of up to three columns fit a phone screen, only scrolling if a long
  // word can't fit its column. Wider ones keep a 720px minimum and scroll
  // sideways below desktop, with a fade on the right edge to show there is more
  const scrolls = table.columns.length > 3

  return (
    <div className="m-auto max-w-screen-xl">
      <div className="container mb-12 max-w-3xl text-center">
        {topContent && <ContentEditor content={topContent} />}
      </div>
      <div className="relative">
        <div
          className={clsx('px-6 2xl:container', {
            'overflow-x-scroll md:overflow-auto': scrolls,
            'overflow-x-auto': !scrolls,
          })}
        >
          <div
            className={clsx('bg-neutral-150 px-6 py-10', {
              'min-w-[720px] lg:min-w-full': scrolls,
            })}
          >
            <div
              className="grid"
              style={gridStyle}
            >
              {table.columns.map((column, i) => (
                <TierColumn
                  key={i}
                  {...column}
                />
              ))}
            </div>
            <div className="my-4 border-b border-neutral-300" />
            <div className="grid gap-4">
              {table.rows.map((row, i) => (
                <TierRow
                  {...row}
                  gridStyle={gridStyle}
                  key={i}
                />
              ))}
            </div>
            {hasFooter && (
              <div
                className="grid pt-8"
                style={gridStyle}
              >
                {table.footer!.map((cell, i) => (
                  <TierFooter
                    key={i}
                    text={cell.text}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {scrolls && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-neutral-150 to-transparent lg:hidden"
          />
        )}
      </div>
      <div className="flex justify-center pt-10">
        {button && <Button {...button} />}
      </div>
    </div>
  )
}
